/**
 * `src/lib/pwa/register.ts` — register the service worker, and surface updates.
 *
 * `vite.config.ts` sets `injectRegister: null` ("registration is hand-written")
 * and nothing wrote it, so the worker was never registered: no offline use,
 * and browsers that require a worker would not offer installation. This is
 * that registration, following SPEC §1.3's update flow:
 *
 *   - An update found WHILE the app is in use waits: the app says «نسخة
 *     جديدة جاهزة» and the person chooses when (it reloads the page, which
 *     would cut off a recitation). Applying it posts SKIP_WAITING (src/sw.ts
 *     listens) and reloads once the new worker controls the page.
 *   - An update already waiting WHEN THE APP OPENS is applied at once, with
 *     no question: nothing is in progress yet, and asking first is what made
 *     a reinstall look broken — the old worker outlives an uninstalled app,
 *     so opening the site again greeted the person with «تحديث» instead of
 *     the app.
 *   - The very first install claims the page (`clientsClaim` in sw.ts) WITHOUT
 *     a reload — a `controllerchange` the user did not ask for is ignored.
 *   - Checked hourly and whenever the app comes back to the foreground.
 *
 * Nothing here runs in `vite dev`, where no worker is built.
 */

import { asset } from '$app/paths';

export type UpdateListener = (ready: boolean) => void;

const listeners = new Set<UpdateListener>();
let waiting: ServiceWorker | null = null;
let requested = false;
let started = false;

/** The longest "the app is still opening" can last; see `registerServiceWorker`. */
const STARTUP_MS = 20_000;

function emit(): void {
	for (const listener of listeners) listener(waiting !== null);
}

function track(worker: ServiceWorker | null): void {
	if (!worker) return;
	waiting = worker;
	emit();
}

/** Apply without asking, and without flashing the notice first. */
function takeNow(worker: ServiceWorker): void {
	requested = true;
	worker.postMessage({ type: 'SKIP_WAITING' });
}

/** Subscribe to "an update is waiting". Called immediately with the state. */
export function onUpdateReady(listener: UpdateListener): () => void {
	listeners.add(listener);
	listener(waiting !== null);
	return () => listeners.delete(listener);
}

/** The person chose to update: hand over to the waiting worker, then reload. */
export function applyUpdate(): void {
	if (!waiting) return;
	requested = true;
	waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker(): void {
	if (started || import.meta.env.DEV) return;
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
	started = true;

	const sw = navigator.serviceWorker;
	let reloading = false;

	/* "When the app opens" lasts until the person first touches or types, or
	 * STARTUP_MS — whichever is sooner. The browser checks for a new worker as
	 * soon as the page registers, so an update deployed since the last visit
	 * usually finishes installing a few seconds in, not before load. */
	let opening = true;
	const settled = () => {
		opening = false;
		removeEventListener('pointerdown', settled, true);
		removeEventListener('keydown', settled, true);
	};
	addEventListener('pointerdown', settled, true);
	addEventListener('keydown', settled, true);
	setTimeout(settled, STARTUP_MS);

	sw.addEventListener('controllerchange', () => {
		// Only an update the person asked for reloads the page.
		if (!requested || reloading) return;
		reloading = true;
		location.reload();
	});

	void sw
		.register(asset('/sw.js'), { updateViaCache: 'none' })
		.then((registration) => {
			// A worker already waiting from an earlier visit: take it now.
			if (registration.waiting && sw.controller) {
				takeNow(registration.waiting);
			}

			registration.addEventListener('updatefound', () => {
				const incoming = registration.installing;
				incoming?.addEventListener('statechange', () => {
					// `controller` is null on the very first install: nothing to replace.
					if (incoming.state !== 'installed' || !sw.controller) return;
					if (opening) takeNow(incoming);
					else track(incoming);
				});
			});

			const check = () => void registration.update().catch(() => {});
			setInterval(check, 60 * 60 * 1000);
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'visible') check();
			});
		})
		.catch(() => {
			// No worker (private mode, blocked storage): the app still works online.
		});
}
